import { Box, HStack, Img, Text, VStack } from '@chakra-ui/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AuthContext } from '../hooks/auth/useAuth'
import { useCurrentSelf } from '../hooks/auth/useCurrentSelf'
import { useRole } from '../hooks/auth/useRole'
import { useFeatureGate } from '../hooks/useFeatureGate'
import { currentSelfQueryOptions, myPermissionsQueryOptions } from '../lib/performance/queryOptions'
import { prefetchPathIntent } from '../lib/performance/prefetch'
import { resolvePlanningPath } from '../lib/navigation/navigationConfig'
import { getNotifications } from '../api/notifications/requests'
import { useTabNotifications } from '../contexts/TabNotificationContext'

import LogoIcon from '../assets/wrk_logo.png'
import { useTranslation } from 'react-i18next'
import {
  House,
  Bell,
  Users,
  Gauge,
  UserPlus,
  Calendar,
  Wallet,
  FileText,
  Briefcase,
  Compass,
  Settings,
  User,
  Building2,
  HelpCircle,
  Sparkles,
  BarChart3,
  UserMinus,
  Mail,
  MessageCircle,
  type LucideIcon,
} from 'lucide-react'

interface NavItemConfig {
  icon: LucideIcon
  title: string
  path: string
  activeRoute: string
  isDisabled: boolean
  isHighlight?: boolean
  /** If set, this nav item is only visible when the org's plan includes this feature */
  featureKey?: string
  notificationCount?: number
}

interface NavSection {
  label: string
  items: NavItemConfig[]
}

interface NavItemProps {
  icon: LucideIcon
  title: string
  path: string
  isDisabled: boolean
  selected: boolean
  isHighlight?: boolean
  notificationCount?: number
  handleClick: (path: string) => void
  handleIntent: (path: string) => void
}

const NavItem: React.FC<NavItemProps> = ({
  icon: IconComponent,
  title,
  path,
  isDisabled,
  selected,
  isHighlight,
  notificationCount,
  handleClick,
  handleIntent,
}) => {
  if (isHighlight) {
    return (
      <HStack
        spacing="10px"
        py="9px"
        px="12px"
        cursor="pointer"
        onClick={() => handleClick(path)}
        onMouseEnter={() => handleIntent(path)}
        onFocus={() => handleIntent(path)}
        bg={selected ? '#D0DDFF4D' : 'transparent'}
        borderRadius="10px"
        transition="all 0.2s ease-in-out"
        _hover={{ bg: '#D0DDFF4D' }}
        css={{
          '& > svg, & > p': {
            background: 'linear-gradient(135deg, #6B67D4 0%, #9B6DFF 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          },
          '& > svg': {
            stroke: '#7B6FE0',
            WebkitTextFillColor: 'initial',
            background: 'none',
          },
        }}
      >
        <IconComponent size={19} strokeWidth={selected ? 2.2 : 2} />
        <Text fontSize="14px" fontWeight="600" lineHeight="20px">
          {title}
        </Text>
        {(notificationCount ?? 0) > 0 && (
          <Box
            minW="16px"
            h="16px"
            borderRadius="full"
            bg="#ae68ff"
            color="white"
            fontSize="9px"
            fontWeight="800"
            lineHeight="16px"
            textAlign="center"
            flexShrink={0}
            ml="auto"
            px="4px"
          >
            {notificationCount! > 99 ? '99+' : notificationCount}
          </Box>
        )}
      </HStack>
    )
  }

  return (
    <HStack
      spacing="10px"
      py="9px"
      px="12px"
      height="40px"
      _hover={{ backgroundColor: isDisabled ? undefined : '#D0DDFF4D' }}
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      color={selected ? 'primary.100' : 'solid.900'}
      onClick={() => !isDisabled && handleClick(path)}
      onMouseEnter={() => !isDisabled && handleIntent(path)}
      onFocus={() => !isDisabled && handleIntent(path)}
      backgroundColor={selected ? '#D0DDFF4D' : 'transparent'}
      borderRadius="10px"
      transition="all 0.2s ease-in-out"
    >
      <IconComponent size={19} strokeWidth={selected ? 2.2 : 2} />
      <Text fontSize="14px" fontWeight={selected ? '600' : '500'} lineHeight="20px">
        {title}
      </Text>
      {(notificationCount ?? 0) > 0 && (
        <Box
          minW="16px"
          h="16px"
          borderRadius="full"
          bg="#ae68ff"
          color="white"
          fontSize="9px"
          fontWeight="800"
          lineHeight="16px"
          textAlign="center"
          flexShrink={0}
          ml="auto"
          px="4px"
        >
          {notificationCount! > 99 ? '99+' : notificationCount}
        </Box>
      )}
    </HStack>
  )
}

const SectionHeader: React.FC<{ label: string }> = ({ label }) => (
  <Text
    fontSize="10.5px"
    fontWeight="600"
    color="solid.400"
    textTransform="uppercase"
    letterSpacing="0.6px"
    px="12px"
    mt="14px"
    mb="2px"
  >
    {label}
  </Text>
)

interface NotificationRouteTab {
  route: string | null
  tab: string | null
}

const getNotificationRouteTab = (type: string, isAdminUser: boolean): NotificationRouteTab => {
  const lower = type.toLowerCase()

  if (lower.includes('self_profile')) {
    return { route: '/user/profile', tab: null }
  }

  if (lower.includes('new_user') || lower.includes('checkin') || lower.includes('contract') || lower.includes('probation') || lower.includes('profile_incomplete')) {
    return { route: '/user/workforce', tab: 'people' }
  }

  if (lower.includes('leave') || lower.includes('schedule')) {
    return {
      route: '/user/planning',
      tab: isAdminUser ? 'leave-management' : 'past-requests',
    }
  }

  if (lower.includes('desk') || lower.includes('room')) {
    return { route: '/user/planning', tab: 'workspace' }
  }

  if (lower.includes('attendance')) {
    return { route: '/user/planning', tab: 'attendance-calendar' }
  }

  if (lower.includes('document_indexing')) {
    return {
      route: '/user/admin/document',
      tab: 'hrinfo',
    }
  }

  if (lower.includes('document')) {
    return {
      route: isAdminUser ? '/user/admin/document' : '/user/document',
      tab: 'documents',
    }
  }

  if (lower.includes('path')) {
    return { route: '/user/explore', tab: 'paths' }
  }

  return { route: null, tab: null }
}

const isNavRouteSelected = (pathname: string, item: NavItemConfig) => {
  if (!item.activeRoute) return false
  if (item.activeRoute === '/user/explore') {
    return pathname.startsWith('/user/explore') || pathname.startsWith('/user/content')
  }
  return pathname.startsWith(item.activeRoute)
}

const NavigationBar: React.FC<{ isInDrawer?: boolean; onNavigate?: () => void }> = ({ isInDrawer = false, onNavigate }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const location = useLocation()
  const navRef = useRef<HTMLDivElement>(null)
  const auth = useContext(AuthContext)
  const user = auth?.user
  const [mailtoClicked, setMailtoClicked] = useState(false)
  const { isAdmin, role: currentRole } = useRole()

  useCurrentSelf(!!user)

  const { data: myPermissions } = useQuery(
    myPermissionsQueryOptions,
  )

  const { hasFeature, hasSubscription } = useFeatureGate()

  const { data: notificationsData } = useQuery({
    queryKey: ['notifications', 'navbar'],
    queryFn: () => getNotifications({ page: 1, page_size: 100 }),
    staleTime: 30000,
    enabled: !!user,
  })

  const { setNotificationRouteCounts, setNotificationTabs } = useTabNotifications()

  const notificationRoutes = useMemo(() => {
    const routes = new Set<string>()
    const items = notificationsData?.data ?? []
    for (const notification of items) {
      if (notification.is_completed || notification.status === 'completed') continue
      const { route } = getNotificationRouteTab(notification.type, isAdmin)
      if (route) routes.add(route)
    }
    return routes
  }, [notificationsData, isAdmin])

  const notificationRouteCounts = useMemo(() => {
    const counts = new Map<string, number>()
    const items = notificationsData?.data ?? []
    for (const notification of items) {
      if (notification.is_completed || notification.status === 'completed') continue
      const { route } = getNotificationRouteTab(notification.type, isAdmin)
      if (route) counts.set(route, (counts.get(route) ?? 0) + 1)
    }
    return counts
  }, [notificationsData, isAdmin])

  const notificationTabs = useMemo(() => {
    const tabs = new Set<string>()
    const items = notificationsData?.data ?? []
    for (const notification of items) {
      if (notification.is_completed || notification.status === 'completed') continue
      const { tab } = getNotificationRouteTab(notification.type, isAdmin)
      if (tab) tabs.add(tab)
    }
    return tabs
  }, [notificationsData, isAdmin])

  useEffect(() => {
    setNotificationRouteCounts(notificationRouteCounts)
  }, [notificationRouteCounts, setNotificationRouteCounts])

  useEffect(() => {
    setNotificationTabs(notificationTabs)
  }, [notificationTabs, setNotificationTabs])

  const hasPlanningAccess = (myPermissions?.planning?.length ?? 0) > 0
  const hasWorkforceAccess = (myPermissions?.workforce?.length ?? 0) > 0
  const hasRecruitmentAccess = (myPermissions?.recruitment?.length ?? 0) > 0
  const hasExploreAccess = (myPermissions?.explore?.length ?? 0) > 0
  const hasHRChatAccess = myPermissions?.support?.includes('hr-chat') ?? false

  // Resolve first available planning tab for nav link
  const planningPath = (() => {
    const allowed = myPermissions?.planning || []
    return resolvePlanningPath(allowed)
  })()

  if (!user) return null

  // --- Bottom-pinned items (always visible) ---
  const aiAssistantItem: NavItemConfig = {
    icon: Sparkles,
    title: t('navigation.aiAssistant', 'AI Assistant'),
    path: '/user/chat',
    activeRoute: '/user/chat',
    isDisabled: false,
    isHighlight: true,
    featureKey: 'ai_assistant',
  }

  const hrChatItem: NavItemConfig = {
    icon: MessageCircle,
    title: t('navigation.hrChat', 'HR Chat'),
    path: '/user/hr-chat',
    activeRoute: '/user/hr-chat',
    isDisabled: false,
  }

  const bottomItems: NavItemConfig[] = [
    {
      icon: HelpCircle,
      title: t('navigation.faq', 'FAQ'),
      path: '/user/faq',
      activeRoute: '/user/faq',
      isDisabled: false,
      featureKey: 'faq',
    },
    {
      icon: Mail,
      title: t('navbar.contactSupport', 'Contact Support'),
      path: 'mailto:support@aboutwrk.com?subject=Support%20Request',
      activeRoute: '',
      isDisabled: false,
    },
    {
      icon: Settings,
      title: t('navigation.settings'),
      path: ['super_admin', 'org_admin'].includes(currentRole || '')
        ? '/user/settings/general'
        : '/user/profile?tab=settings',
      activeRoute: ['super_admin', 'org_admin'].includes(currentRole || '') ? '/user/settings' : '',
      isDisabled: false,
    },
  ]

  // --- Section configs per role ---
  const sectionsOrgAdmin: NavSection[] = [
    {
      label: t('navigation.sectionMain', 'Main'),
      items: [
        { icon: House, title: t('navigation.home'), path: '/user/home', activeRoute: '/user/home', isDisabled: false },
        {
          icon: Bell,
          title: t('navigation.todo'),
          path: '/user/todo',
          activeRoute: '/user/todo',
          isDisabled: false,
        },
      ],
    },

    {
      label: t('navigation.sectionPeople', 'People'),
      items: [
        {
          icon: Users,
          title: t('navigation.workforce'),
          path: '/user/workforce/people',
          activeRoute: '/user/workforce',
          isDisabled: false,
          featureKey: 'workforce',
        },
        {
          icon: Gauge,
          title: t('navigation.evaluations'),
          path: '/user/evaluations',
          activeRoute: '/user/evaluations',
          isDisabled: false,
          featureKey: 'evaluations',
        },
        {
          icon: UserPlus,
          title: t('navigation.recruitment', 'Recruitment'),
          path: '/user/recruitment/candidates',
          activeRoute: '/user/recruitment',
          isDisabled: false,
          featureKey: 'recruitment',
        },
        {
          icon: UserMinus,
          title: t('navigation.outflow', 'Outflow'),
          path: '/user/outflow/exit-management',
          activeRoute: '/user/outflow',
          isDisabled: false,
          featureKey: 'outflow',
        },
        {
          icon: BarChart3,
          title: t('navigation.analytics', 'Analytics'),
          path: '/user/analytics',
          activeRoute: '/user/analytics',
          isDisabled: false,
          featureKey: 'analytics',
        },
      ],
    },
    {
      label: t('navigation.sectionManage', 'Manage'),
      items: [
        {
          icon: Calendar,
          title: t('navigation.planning'),
          path: planningPath,
          activeRoute: '/user/planning',
          isDisabled: false,
          featureKey: 'scheduling',
        },
        {
          icon: Wallet,
          title: t('navigation.payroll'),
          path: '/user/payroll/people',
          activeRoute: '/user/payroll',
          isDisabled: false,
          featureKey: 'payroll',
        },
        {
          icon: FileText,
          title: t('navigation.documents'),
          path: '/user/admin/document/documents',
          activeRoute: '/user/admin/document',
          isDisabled: false,
          featureKey: 'documents_signing',
        },
        {
          icon: Briefcase,
          title: t('navigation.classifications'),
          path: '/user/classification/jobprofile',
          activeRoute: '/user/classification',
          isDisabled: false,
          featureKey: 'classifications',
        },
        //      {
        //       icon: Wallet,
        //        title: t('Payments'),
        //        path: '/user/settings/payments',
        //        activeRoute: '/user/settings/payments',
        //        isDisabled: false,
        //      },
      ],
    },
    ...(hasExploreAccess
      ? [
          {
            label: t('navigation.sectionLearn', 'Learn'),
            items: [
              {
                icon: Compass,
                title: t('navigation.explore'),
                path: '/user/explore/content',
                activeRoute: '/user/explore',
                isDisabled: false,
                featureKey: 'explore',
              },
            ],
          },
        ]
      : []),
  ]

  const sectionsCountryAdmin: NavSection[] = [
    {
      label: t('navigation.sectionMain', 'Main'),
      items: [
        { icon: House, title: t('navigation.home'), path: '/user/home', activeRoute: '/user/home', isDisabled: false },
      ],
    },
    {
      label: t('navigation.sectionPeople', 'People'),
      items: [
        ...(hasWorkforceAccess
          ? [
              {
                icon: Users,
                title: t('navigation.workforce'),
                path: '/user/workforce/people',
                activeRoute: '/user/workforce',
                isDisabled: false,
                featureKey: 'workforce',
              },
            ]
          : []),
        {
          icon: Gauge,
          title: t('navigation.evaluations'),
          path: '/user/evaluations',
          activeRoute: '/user/evaluations',
          isDisabled: false,
        },
        ...(hasRecruitmentAccess
          ? [
              {
                icon: UserPlus,
                title: t('navigation.recruitment', 'Recruitment'),
                path: '/user/recruitment/candidates',
                activeRoute: '/user/recruitment',
                isDisabled: false,
                featureKey: 'recruitment',
              },
            ]
          : []),
        {
          icon: BarChart3,
          title: t('navigation.analytics', 'Analytics'),
          path: '/user/analytics',
          activeRoute: '/user/analytics',
          isDisabled: false,
        },
      ],
    },
    {
      label: t('navigation.sectionManage', 'Manage'),
      items: [
        ...(hasPlanningAccess
          ? [
              {
                icon: Calendar,
                title: t('navigation.planning'),
                path: planningPath,
                activeRoute: '/user/planning',
                isDisabled: false,
              } as NavItemConfig,
            ]
          : []),
        {
          icon: Wallet,
          title: t('navigation.payroll'),
          path: '/user/payroll/people',
          activeRoute: '/user/payroll',
          isDisabled: false,
        },
        {
          icon: FileText,
          title: t('navigation.documents'),
          path: '/user/admin/document/documents',
          activeRoute: '/user/admin/document',
          isDisabled: false,
        },
      ],
    },
    ...(hasExploreAccess
      ? [
          {
            label: t('navigation.sectionLearn', 'Learn'),
            items: [
              {
                icon: Compass,
                title: t('navigation.explore'),
                path: '/user/explore/content',
                activeRoute: '/user/explore',
                isDisabled: false,
                featureKey: 'explore',
              },
            ],
          },
        ]
      : []),
  ]

  const sectionsFinance: NavSection[] = [
    {
      label: t('navigation.sectionMain', 'Main'),
      items: [
        { icon: House, title: t('navigation.home'), path: '/user/home', activeRoute: '/user/home', isDisabled: false },
      ],
    },
    {
      label: t('navigation.sectionPeople', 'People'),
      items: [
        ...(hasWorkforceAccess
          ? [
              {
                icon: Users,
                title: t('navigation.workforce'),
                path: '/user/workforce/people',
                activeRoute: '/user/workforce',
                isDisabled: false,
                featureKey: 'workforce',
              },
            ]
          : []),
        ...(hasRecruitmentAccess
          ? [
              {
                icon: UserPlus,
                title: t('navigation.recruitment', 'Recruitment'),
                path: '/user/recruitment/candidates',
                activeRoute: '/user/recruitment',
                isDisabled: false,
                featureKey: 'recruitment',
              },
            ]
          : []),
        {
          icon: BarChart3,
          title: t('navigation.analytics', 'Analytics'),
          path: '/user/analytics',
          activeRoute: '/user/analytics',
          isDisabled: false,
        },
      ],
    },
    {
      label: t('navigation.sectionManage', 'Manage'),
      items: [
        ...(hasPlanningAccess
          ? [
              {
                icon: Calendar,
                title: t('navigation.planning'),
                path: planningPath,
                activeRoute: '/user/planning',
                isDisabled: false,
              } as NavItemConfig,
            ]
          : []),
        {
          icon: Wallet,
          title: t('navigation.payroll'),
          path: '/user/payroll/people',
          activeRoute: '/user/payroll',
          isDisabled: false,
        },
        {
          icon: FileText,
          title: t('navigation.documents'),
          path: '/user/admin/document/documents',
          activeRoute: '/user/admin/document',
          isDisabled: false,
        },
      ],
    },
    ...(hasExploreAccess
      ? [
          {
            label: t('navigation.sectionLearn', 'Learn'),
            items: [
              {
                icon: Compass,
                title: t('navigation.explore'),
                path: '/user/explore/content',
                activeRoute: '/user/explore',
                isDisabled: false,
                featureKey: 'explore',
              },
            ],
          },
        ]
      : []),
  ]

  const sectionsTeamLeader: NavSection[] = [
    {
      label: t('navigation.sectionMain', 'Main'),
      items: [
        { icon: House, title: t('navigation.home'), path: '/user/home', activeRoute: '/user/home', isDisabled: false },
      ],
    },
    {
      label: t('navigation.sectionPeople', 'People'),
      items: [
        ...(hasWorkforceAccess
          ? [
              {
                icon: Users,
                title: t('navigation.workforce'),
                path: '/user/workforce/people',
                activeRoute: '/user/workforce',
                isDisabled: false,
                featureKey: 'workforce',
              },
            ]
          : []),
          ...(hasRecruitmentAccess
          ? [
              {
                icon: UserPlus,
                title: t('navigation.recruitment', 'Recruitment'),
                path: '/user/recruitment/candidates',
                activeRoute: '/user/recruitment',
                isDisabled: false,
                featureKey: 'recruitment',
              },
            ]
          : []),
      ],
    },
    {
      label: t('navigation.sectionManage', 'Manage'),
      items: [
        ...(hasPlanningAccess
          ? [
              {
                icon: Calendar,
                title: t('navigation.planning'),
                path: planningPath,
                activeRoute: '/user/planning',
                isDisabled: false,
              } as NavItemConfig,
            ]
          : []),
        {
          icon: FileText,
          title: t('navigation.documents'),
          path: '/user/admin/document/documents',
          activeRoute: '/user/admin/document',
          isDisabled: false,
        },
      ],
    },
    ...(hasExploreAccess
      ? [
          {
            label: t('navigation.sectionLearn', 'Learn'),
            items: [
              {
                icon: Compass,
                title: t('navigation.explore'),
                path: '/user/explore/content',
                activeRoute: '/user/explore',
                isDisabled: false,
                featureKey: 'explore',
              },
            ],
          },
        ]
      : []),
  ]

  const sectionsUser: NavSection[] = [
    {
      label: t('navigation.sectionMain', 'Main'),
      items: [
        { icon: House, title: t('navigation.home'), path: '/user/home', activeRoute: '/user/home', isDisabled: false },
        {
          icon: User,
          title: t('navigation.myProfile'),
          path: '/user/profile',
          activeRoute: '/user/profile',
          isDisabled: false,
        },
      ],
    },
    {
      label: t('navigation.sectionManage', 'Manage'),
      items: [
        {
          icon: Building2,
          title: t('navigation.organization'),
          path: '/user/organization',
          activeRoute: '/user/organization',
          isDisabled: false,
        },
        {
          icon: FileText,
          title: t('navigation.documents'),
          path: '/user/document/documents',
          activeRoute: '/user/document',
          isDisabled: false,
        },
        ...(hasPlanningAccess
          ? [
              {
                icon: Calendar,
                title: t('navigation.planning'),
                path: planningPath,
                activeRoute: '/user/planning',
                isDisabled: false,
              } as NavItemConfig,
            ]
          : []),
      ],
    },
    ...(hasExploreAccess
      ? [{
          label: t('navigation.sectionLearn', 'Learn'),
          items: [
            {
              icon: Compass,
              title: t('navigation.explore'),
              path: '/user/explore/content',
              activeRoute: '/user/explore',
              isDisabled: false,
              featureKey: 'explore',
            },
          ],
        }]
      : []),
  ]

  // Select sections based on role
  let sections: NavSection[]
  if (!isAdmin) {
    sections = sectionsUser
  } else {
    switch (currentRole) {
      case 'super_admin':
      case 'org_admin':
        sections = sectionsOrgAdmin
        break
      case 'country_admin':
        sections = sectionsCountryAdmin
        break
      case 'finance':
        sections = sectionsFinance
        break
      case 'team_leader':
        sections = sectionsTeamLeader
        break
      default:
        sections = sectionsUser
        break
    }
  }
  if (isInDrawer) {
    sections = sections.filter((section) => section.label !== t('navigation.sectionLearn', 'Learn'))
  }

  // Filter nav items by subscription features (super_admin always sees all)
  if (currentRole !== 'super_admin') {
    sections = sections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => !item.featureKey || hasFeature(item.featureKey)),
      }))
      .filter((section) => section.items.length > 0)
  }

  let visibleBottomItems = ['super_admin', 'org_admin'].includes(currentRole || '')
    ? bottomItems
    : bottomItems.filter((item) => item.path !== '/user/settings/general')

  if (hasHRChatAccess) {
    visibleBottomItems = [hrChatItem, ...visibleBottomItems]
  }

  if (currentRole !== 'super_admin') {
    visibleBottomItems = visibleBottomItems.filter((item) => !item.featureKey || hasFeature(item.featureKey))
  }

  const handleNavigation = (path: string) => {
    if (path.startsWith('mailto:')) {
      setMailtoClicked(true)
      window.open(path, '_blank')
      setTimeout(() => setMailtoClicked(false), 300)
    } else {
      navigate(path)
    }
    onNavigate?.()
  }

  const handleIntent = (path: string) => {
    prefetchPathIntent(path, queryClient)
    if (
      path.startsWith('/user/planning') ||
      path.startsWith('/user/workforce') ||
      path.startsWith('/user/recruitment')
    ) {
      void queryClient.prefetchQuery(currentSelfQueryOptions(queryClient, !!user))
    }
  }

  return (
    <Box
      ref={navRef}
      h={isInDrawer ? '100%' : '100vh'}
      backgroundColor="background.White"
      display={isInDrawer ? 'flex' : { base: 'none', md: 'flex' }}
      flexDirection="column"
      width={isInDrawer ? '100%' : '230px'}
      minW={isInDrawer ? undefined : '230px'}
      borderRight={isInDrawer ? 'none' : '1px solid'}
      borderRightColor="solid.50"
      px="12px"
      py="16px"
    >
      {/* Logo */}
      <Box mt="8px" mb="8px" display="flex" justifyContent="center">
        <Img src={LogoIcon} alt="Logo" width="100px" height="50px" />
      </Box>

      {/* Scrollable sections */}
      <VStack
        flex="1"
        align="stretch"
        spacing="2px"
        overflowY="auto"
        css={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.08)', borderRadius: '2px' },
        }}
      >
        {sections.map((section) => (
          <Box key={section.label}>
            <SectionHeader label={section.label} />
            {section.items.map((item) => (
              <NavItem
                key={item.path}
                icon={item.icon}
                title={item.title}
                path={item.path}
                isDisabled={item.isDisabled}
                selected={isNavRouteSelected(location.pathname, item)}
                notificationCount={notificationRouteCounts.get(item.activeRoute) ?? 0}
                handleClick={handleNavigation}
                handleIntent={handleIntent}
              />
            ))}
          </Box>
        ))}
      </VStack>

      <Box pt="4px">
        <NavItem
          icon={aiAssistantItem.icon}
          title={aiAssistantItem.title}
          path={aiAssistantItem.path}
          isDisabled={aiAssistantItem.isDisabled}
          isHighlight={aiAssistantItem.isHighlight}
          selected={isNavRouteSelected(location.pathname, aiAssistantItem)}
          notificationCount={notificationRouteCounts.get(aiAssistantItem.activeRoute) ?? 0}
          handleClick={handleNavigation}
          handleIntent={handleIntent}
        />
      </Box>

      {/* Bottom-pinned items */}
      <Box borderTop="1px solid" borderColor="solid.50" pt="8px" mt="4px">
        {visibleBottomItems.map((item) => (
          <NavItem
            key={item.path}
            icon={item.icon}
            title={item.title}
            path={item.path}
            isDisabled={item.isDisabled}
            isHighlight={item.isHighlight}
            selected={item.path.startsWith('mailto:') ? mailtoClicked : isNavRouteSelected(location.pathname, item)}
            notificationCount={notificationRouteCounts.get(item.activeRoute) ?? 0}
            handleClick={handleNavigation}
            handleIntent={handleIntent}
          />
        ))}
      </Box>
    </Box>
  )
}

export default NavigationBar
